import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './cart.module.css';
import { updateQuantity, removeFromCart, clearCart } from '../../redux/slice/cartSlice';
import { usePaymentManagement, useCreateOrderFromCart } from '../../api';
import { toast } from 'react-toastify';
import { Button } from 'glintly-ui';
import Container from '../../components/container';
import { openAuthModal } from '../../utils/openAuthModal';

function variantTags(variants) {
  if (!variants) return [];
  if (Array.isArray(variants)) {
    return variants
      .map((entry) => {
        if (entry == null) return '';
        if (typeof entry === 'object') {
          const label = entry.name || entry.label || '';
          const value = entry.value || entry.option || '';
          if (label && value) return `${label}: ${value}`;
          if (value) return String(value);
          return '';
        }
        return '';
      })
      .filter(Boolean);
  }
  if (typeof variants === 'object') {
    return Object.entries(variants)
      .filter(([key]) => !/^\d+$/.test(key))
      .map(([key, value]) => {
        if (value == null || value === '') return '';
        if (typeof value === 'object') {
          const inner = value.name || value.value || value.label;
          return inner ? `${key}: ${inner}` : '';
        }
        return `${key}: ${value}`;
      })
      .filter(Boolean);
  }
  return [];
}

const Cart = () => {
  const items = useSelector((state) => state.cart.items);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [paymentError, setPaymentError] = useState('');

  const {
    createOrder,
    verifyPayment,
    handlePaymentSuccess,
    isCreatingOrder,
    isVerifying,
    isHandlingSuccess,
  } = usePaymentManagement();

  const { mutateAsync: createOrderFromCart, isPending: isCreatingOrderFromCart } =
    useCreateOrderFromCart();

  const isProcessingPayment =
    isCreatingOrder || isVerifying || isHandlingSuccess || isCreatingOrderFromCart;

  const handleQuantityChange = (productId, variants, change) => {
    const item = items.find(
      (item) =>
        item.productId === productId && JSON.stringify(item.variants) === JSON.stringify(variants)
    );
    if (item) {
      const newQuantity = item.quantity + change;
      if (newQuantity > 0) {
        dispatch(updateQuantity({ productId, quantity: newQuantity, variants }));
      }
    }
  };

  const handleRemove = (productId, variants) => {
    dispatch(removeFromCart({ productId, variants }));
  };

  const handleClearCart = () => {
    dispatch(clearCart());
  };

  // Calculate totals for all items in cart
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const deliveryFee = subtotal >= 500 ? 0 : 40;
  const total = subtotal + deliveryFee;

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          resolve(true);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  // Handle place order
  const handlePlaceOrder = async () => {
    if (!user) {
      openAuthModal('signin');
      setPaymentError('Please sign in to place an order');
      setTimeout(() => setPaymentError(''), 3000);
      return;
    }

    if (items.length === 0) {
      setPaymentError('Your cart is empty');
      setTimeout(() => setPaymentError(''), 3000);
      return;
    }

    setPaymentError('');

    try {
      const rawId = user._id ?? user.id;
      const customerId =
        rawId != null && typeof rawId === 'object'
          ? String(rawId._id ?? rawId.id ?? '')
          : rawId != null
            ? String(rawId)
            : '';

      if (!customerId) {
        setPaymentError('Missing user id — please log out and log in again.');
        setTimeout(() => setPaymentError(''), 5000);
        return;
      }

      // Prepare order data (customerId must be a string for Razorpay notes + DB)
      const orderData = {
        amount: total,
        orderId: `ORD_${Date.now().toString().slice(-8)}_${Math.random()
          .toString(36)
          .substr(2, 6)}`,
        customerId,
        customerName: user.name || user.firstName || 'Customer',
        customerEmail: user.email || '',
        customerMobile: user.mobile || '',
        productInfo: items.map((item) => item.name).join(', '),
        items: items,
      };

      // Create order on backend
      const orderResponse = await createOrder(orderData);

      if (!orderResponse.success) {
        throw new Error(orderResponse.error || 'Failed to create order');
      }

      // Prepare Razorpay options
      const options = {
        key: orderResponse.key,
        amount: orderResponse.amount * 100, // Convert to paise
        currency: 'INR',
        name: 'Barber Shop',
        description: `Order for ${items.length} items`,
        order_id: orderResponse.orderId,
        handler: async function (response) {
          try {
            // Verify payment on backend
            const verifyResponse = await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.success) {
              // Payment successful
              await handlePaymentSuccess({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
              });

              // Create database order
              try {
                console.log('Creating database order with data:', {
                  userId: user._id || user.id,
                  items: items,
                  total: total,
                  paymentId: response.razorpay_payment_id,
                  paymentStatus: 'SUCCESS',
                  customerName: user.name || user.firstName || 'Customer',
                  customerEmail: user.email || 'customer@example.com',
                  customerMobile: user.mobile || user.phone || '0000000000',
                });

                const orderResult = await createOrderFromCart({
                  userId: customerId,
                  items: items,
                  total: total,
                  paymentId: response.razorpay_payment_id,
                  paymentStatus: 'SUCCESS',
                  customerName: user.name || user.firstName || 'Customer',
                  customerEmail: user.email || 'customer@example.com',
                  customerMobile: user.mobile || user.phone || '0000000000',
                });

                console.log('Order created successfully:', orderResult);
              } catch (orderError) {
                console.error('Error creating database order:', orderError);
                // Don't fail the payment if order creation fails
                toast.warning(
                  'Payment successful but order creation failed. Please contact support.'
                );
              }

              // Clear all items from cart
              dispatch(clearCart());

              // Show success message and redirect
              toast.success('Payment successful! Your order has been placed.');
              navigate('/profile'); // Redirect to profile/orders page
            } else {
              toast.error(verifyResponse.error || 'Payment verification failed');
              throw new Error(verifyResponse.error || 'Payment verification failed');
            }
          } catch (verifyError) {
            console.error('Payment verification error:', verifyError);
            setPaymentError('Payment verification failed. Please contact support.');
            setTimeout(() => setPaymentError(''), 5000);
          }
        },
        prefill: {
          name: orderData.customerName,
          email: orderData.customerEmail,
          contact: orderData.customerMobile,
        },
        theme: {
          color: '#DC2626', // Primary color from theme
        },
        modal: {
          ondismiss: () => {
            // Modal dismissed
          },
        },
      };

      // Open Razorpay checkout
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'Payment failed. Please try again.');
      setTimeout(() => setPaymentError(''), 5000);
    }
  };

  return (
    <Container
      title="Shopping cart"
      subtitle={`${items.length} ${items.length === 1 ? 'item' : 'items'} in your cart`}
    >
      {items.length === 0 ? (
        <div className={styles.emptyCart}>
          <div className={styles.emptyCartIcon}>🛒</div>
          <h2>Your cart is empty</h2>
          <p>Looks like you haven't added any items to your cart yet.</p>
          <button
            className={styles.continueShoppingBtn}
            type="button"
            onClick={() => navigate('/services')}
          >
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className={styles.cartContent}>
          {/* Cart Items */}
          <div className={styles.cartItems}>
            {/* Items List */}
            {items.map((item, idx) => {
              return (
                <div
                  key={item.productId + JSON.stringify(item.variants) + idx}
                  className={styles.cartItem}
                >
                  <div className={styles.itemImage}>
                    {item.image ? (
                      <img
                        src={item.image}
                        alt=""
                        onError={(event) => {
                          event.currentTarget.style.display = 'none';
                          const fallback = event.currentTarget.nextElementSibling;
                          if (fallback) fallback.hidden = false;
                        }}
                      />
                    ) : null}
                    <div className={styles.placeholderImage} hidden={Boolean(item.image)}>
                      ✂️
                    </div>
                  </div>

                  <div className={styles.itemDetails}>
                    <h3 className={styles.itemName}>{item.name}</h3>
                    {variantTags(item.variants).length > 0 && (
                      <div className={styles.itemVariants}>
                        {variantTags(item.variants).map((label) => (
                          <span key={label} className={styles.variantTag}>
                            {label}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className={styles.itemPrice}>₹{item.price}</div>
                  </div>

                  <div className={styles.quantityControls}>
                    <button
                      className={styles.quantityBtn}
                      onClick={() => handleQuantityChange(item.productId, item.variants, -1)}
                      disabled={item.quantity <= 1}
                    >
                      −
                    </button>
                    <span className={styles.quantityValue}>{item.quantity}</span>
                    <button
                      className={styles.quantityBtn}
                      onClick={() => handleQuantityChange(item.productId, item.variants, 1)}
                    >
                      +
                    </button>
                  </div>

                  <div className={styles.itemTotal}>
                    <div className={styles.totalPrice}>₹{item.price * item.quantity}</div>
                    <button
                      className={styles.removeBtn}
                      onClick={() => handleRemove(item.productId, item.variants)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Price Summary */}
          <div className={styles.priceSummary}>
            <h3 className={styles.summaryTitle}>Price Details</h3>

            <div className={styles.priceRow}>
              <span>Subtotal ({items.length} items)</span>
              <span>₹{subtotal}</span>
            </div>

            <div className={styles.priceRow}>
              <span>Delivery Fee</span>
              <span className={deliveryFee === 0 ? styles.freeDelivery : ''}>
                {deliveryFee === 0 ? 'FREE' : `₹${deliveryFee}`}
              </span>
            </div>

            {subtotal < 500 && (
              <div className={styles.savingsNote}>
                Add ₹{500 - subtotal} more to get FREE delivery
              </div>
            )}

            <div className={styles.totalRow}>
              <span>Total Amount</span>
              <span>₹{total}</span>
            </div>

            {deliveryFee === 0 ? (
              <div className={styles.savings}>You save ₹40 on delivery charges</div>
            ) : null}

            {paymentError && <div className={styles.errorMessage}>{paymentError}</div>}

            <Button
              fullWidth
              variant="primary"
              className={styles.checkoutBtn}
              disabled={items.length === 0 || isProcessingPayment}
              onClick={handlePlaceOrder}
            >
              {isProcessingPayment
                ? 'Processing...'
                : user
                  ? 'Place Order'
                  : 'Sign in to place order'}
            </Button>

            <Button
              fullWidth
              variant="gray"
              border
              shade={200}
              className={styles.clearCartBtn}
              onClick={handleClearCart}
            >
              Clear Cart
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Cart;
